/**
 * Web Platform Services Configuration
 *
 * This module configures dependency injection for the web platform,
 * registering platform-specific implementations like WebTokenStorageService.
 * It also configures the auth error callback to handle session expiration.
 */

import * as Sentry from '@sentry/react';

import {
  registerTokenStorage,
  registerStorageService,
  services,
  updateIntegrationConfig,
  registerObservability,
  SentryObservabilityProvider,
  trackObservabilityUser,
} from '@personal-trainer-app/shared';

import type { AuthErrorInterface } from '@personal-trainer-app/shared/src/types/api';

import { WebTokenStorageService } from '@/services/auth/token.storage.service';
import { authEvents } from '@/services/auth-events';
import { WebStorageService } from '@/services/storage.service';
import { useAuthStore } from '@/stores';
import {
  goToAdminPanel,
  impersonatorOf,
  watchImpersonationEnd,
} from '@/utils/impersonation';

// =============================================================================
// AUTH ERROR HANDLER
// =============================================================================

/**
 * Handle unrecoverable authentication errors from the API layer.
 * This is called when token refresh fails and the user needs to re-authenticate.
 */
function handleAuthError(error: AuthErrorInterface): void {
  // An admin signed in as a member: the 30-minute window has closed and the
  // browser is the admin again — still signed in, so not the login page. Read
  // before anything clears the user, which is where the flag lives.
  if (impersonatorOf(useAuthStore.getState().user)) {
    goToAdminPanel();
    return;
  }

  console.warn(
    '[WebServices] Auth error received, session expired:',
    error.message || 'Unknown error'
  );

  // Emit the auth error event for consumers to react (e.g., redirect to login)
  authEvents.emit('auth:error', {
    error,
    message: error.message || 'Session expired',
  });

  // Emit specific session expired event
  authEvents.emit('auth:session-expired', {
    error,
    message: 'Your session has expired. Please log in again.',
  });

  // Clear the auth state via the store
  // Using getState() to avoid React hook rules since this runs outside components
  const authStore = useAuthStore.getState();
  authStore.clearUser();
  authStore.clearTokens();
}

// =============================================================================
// WEB SERVICES INITIALIZATION
// =============================================================================

/**
 * Initialize services with web-specific implementations
 */
export function initializeWebServices() {
  // Register the web-specific token storage implementation
  registerTokenStorage(WebTokenStorageService);

  // Register the web-specific storage implementation
  registerStorageService(WebStorageService);

  // Configure the auth error callback in the integration layer
  // This ensures auth errors from API calls trigger proper logout and events
  updateIntegrationConfig({
    auth: {
      onAuthError: handleAuthError,
    },
  });

  // Send the shared observability manager's reports (failed requests, error
  // boundaries) to Sentry. The manager is reconfigured in place, so the API
  // layer's long-lived services see it too. With no DSN the SDK is off and
  // these calls do nothing.
  registerObservability(new SentryObservabilityProvider(Sentry));
  // The signed-in account's username, now and at every later session change.
  trackObservabilityUser();
  // An admin signed in as a member: wherever the session ends, the way out
  // is the admin panel.
  watchImpersonationEnd();

  console.log(
    '🌐 Web services initialized with secure token storage, localStorage-based storage, and auth error handling'
  );
}

/**
 * Get web-configured services
 * These services now use WebTokenStorageService for secure storage and WebStorageService for general storage
 */
export function getWebServices() {
  return services;
}

// =============================================================================
// AUTH EVENTS RE-EXPORT
// =============================================================================

export { authEvents } from '@/services/auth-events';
export type {
  AuthErrorEventType,
  AuthEventPayload,
} from '@/services/auth-events';
